// src/pages/Messages.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Grid,
  ButtonGroup,
  Tooltip,
  TextField,
  Checkbox,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Flag as FlagIcon,
  FlagOutlined as FlagOutlinedIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Block as BlockIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Undo as UndoIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoneyOff as MoneyOffIcon,
  CheckCircle as CheckCircleOutlineIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { reportingAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useMessages } from '../context/MessagesContext';
import { format } from 'date-fns';

const Messages = () => {
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('client_id');
  const filterParam = searchParams.get('filter');
  const { user } = useAuth();
  const { setStats } = useMessages();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // All messages loaded from server (full dataset)
  const [allMessages, setAllMessages] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');

  // UI state
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [highRiskOnly, setHighRiskOnly] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchText, setSearchText] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [channelFilter, setChannelFilter] = useState('all');
  const [selectedClientId, setSelectedClientId] = useState(clientIdFromUrl || 'all');
  const [clients, setClients] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [acknowledgeLoading, setAcknowledgeLoading] = useState(false);
  const [moneySentLoading, setMoneySentLoading] = useState(false);
  const [piiDisclosedLoading, setPiiDisclosedLoading] = useState(false);
  const [acknowledgePiiLoading, setAcknowledgePiiLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm }

  // Fetch ALL messages from server (once on mount, then silent polling)
  const fetchAllMessages = useCallback(async (silent = false) => {
    try {
      if (!silent) setError('');
      const response = await reportingAPI.getHistory({
        limit: 10000,
        exclude_training: true,
      });
      setAllMessages(response.data.events || response.data.messages || []);
    } catch (err) {
      if (!silent) {
        console.error('Error loading messages:', err);
        setError('Failed to load messages');
      }
    } finally {
      if (!silent) setInitialLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAllMessages();
  }, [fetchAllMessages]);

  // Fetch linked clients for dropdown (caregivers only)
  useEffect(() => {
    if (user?.role === 'caregiver' || user?.role === 'both') {
      userAPI.getClients()
        .then(res => setClients(res.data.clients || []))
        .catch(err => console.error('Failed to load clients:', err));
    }
  }, [user]);

  // Silent polling every 30 seconds — no loading flash, no focus loss
  useEffect(() => {
    const interval = setInterval(() => fetchAllMessages(true), 30000);
    return () => clearInterval(interval);
  }, [fetchAllMessages]);

  // Apply filter from URL params on mount
  useEffect(() => {
    if (filterParam) {
      setQuickFilter(filterParam);
    }
  }, []);

  // Client-side filtering: search, channel, person, dates, risk, quick filters
  const filteredMessages = useMemo(() => {
    return allMessages.filter(message => {
      // Channel filter
      if (channelFilter !== 'all' && message.channel !== channelFilter) return false;

      // Client filter
      if (selectedClientId !== 'all' && String(message.user_id) !== String(selectedClientId)) return false;

      // High risk only
      if (highRiskOnly && !message.high_risk) return false;

      // Date range
      if (startDate) {
        const msgDate = new Date(message.received_at).toISOString().split('T')[0];
        if (msgDate < startDate) return false;
      }
      if (endDate) {
        const msgDate = new Date(message.received_at).toISOString().split('T')[0];
        if (msgDate > endDate) return false;
      }

      // Quick filters
      if (activeQuickFilter === 'unread' && message.is_read) return false;
      if (activeQuickFilter === 'flagged' && !message.is_flagged) return false;
      if (activeQuickFilter === 'money_sent' && !message.money_sent) return false;
      if (activeQuickFilter === 'pii_disclosed' && !message.pii_disclosed) return false;

      // Search text (instant, no debounce needed)
      if (searchText) {
        const s = searchText.toLowerCase();
        const matchesText = message.message_text?.toLowerCase().includes(s);
        const matchesUser = message.user_name?.toLowerCase().includes(s);
        const matchesChannel = message.channel?.toLowerCase().includes(s);
        if (!matchesText && !matchesUser && !matchesChannel) return false;
      }

      return true;
    });
  }, [allMessages, channelFilter, selectedClientId, highRiskOnly, startDate, endDate, activeQuickFilter, searchText]);

  // Client-side pagination
  const totalMessages = filteredMessages.length;
  const paginatedMessages = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredMessages.slice(start, start + rowsPerPage);
  }, [filteredMessages, page, rowsPerPage]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [channelFilter, selectedClientId, highRiskOnly, startDate, endDate, activeQuickFilter, searchText]);

  // Quick filter functions
  const setQuickFilter = (filter) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    switch(filter) {
      case 'today':
        setStartDate(todayStr);
        setEndDate(todayStr);
        setHighRiskOnly(false);
        setActiveQuickFilter('today');
        setPage(0);
        break;
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        setStartDate(weekAgo.toISOString().split('T')[0]);
        setEndDate(todayStr);
        setHighRiskOnly(false);
        setActiveQuickFilter('week');
        setPage(0);
        break;
      case 'high-risk':
        setHighRiskOnly(true);
        setStartDate('');
        setEndDate('');
        setActiveQuickFilter('high-risk');
        setPage(0);
        break;
      case 'unread':
        setHighRiskOnly(false);
        setStartDate('');
        setEndDate('');
        setActiveQuickFilter('unread');
        setPage(0);
        break;
      case 'flagged':
        setHighRiskOnly(false);
        setStartDate('');
        setEndDate('');
        setActiveQuickFilter('flagged');
        setPage(0);
        break;
      case 'money_sent':
        setHighRiskOnly(false);
        setStartDate('');
        setEndDate('');
        setActiveQuickFilter('money_sent');
        setPage(0);
        break;
      case 'pii_disclosed':
        setHighRiskOnly(false);
        setStartDate('');
        setEndDate('');
        setActiveQuickFilter('pii_disclosed');
        setPage(0);
        break;
      case 'clear':
        setStartDate('');
        setEndDate('');
        setHighRiskOnly(false);
        setSearchText('');
        setChannelFilter('all');
        setSelectedClientId('all');
        setActiveQuickFilter(null);
        setPage(0);
        break;
      default:
        break;
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        high_risk_only: highRiskOnly,
      };

      if (selectedClientId && selectedClientId !== 'all') params.client_id = selectedClientId;
      if (channelFilter !== 'all') params.channel = channelFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      // Get the access token
      const token = localStorage.getItem('accessToken');
      
      // Create query string
      const queryString = new URLSearchParams(params).toString();
      
      // Fetch with auth header
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/reporting/export?${queryString}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob and download it
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `second-clear-messages-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export messages');
    }
  };

  // Bulk action handlers
  const handleSelectAll = () => {
    if (selectedMessages.length === paginatedMessages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(paginatedMessages.map(m => m.id));
    }
  };

  const handleSelectMessage = (messageId) => {
    setSelectedMessages(prev => {
      if (prev.includes(messageId)) {
        return prev.filter(id => id !== messageId);
      } else {
        return [...prev, messageId];
      }
    });
  };

  const handleBulkUpdate = async (updates) => {
    setBulkActionLoading(true);
    // Optimistic update
    setAllMessages(prev =>
      prev.map(msg => selectedMessages.includes(msg.id) ? { ...msg, ...updates } : msg)
    );

    // Sync unread badge count in context
    if (updates.is_read !== undefined) {
      const readChange = selectedMessages.length;
      setStats(prev => prev ? {
        ...prev,
        unread_count: prev.unread_count + (updates.is_read ? -readChange : readChange)
      } : prev);
    }

    const ids = [...selectedMessages];
    setSelectedMessages([]);
    try {
      await Promise.all(ids.map(id => reportingAPI.updateMessageStatus(id, updates)));
    } catch (err) {
      console.error('Bulk update error:', err);
      setError('Failed to update messages');
      fetchAllMessages(true); // silent revert
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedMessage) return;
    
    setNoteLoading(true);
    try {
      const response = await reportingAPI.addNote(selectedMessage.id, newNote.trim());
      setNotes([...notes, response.data.note]);
      setNewNote('');
    } catch (err) {
      console.error('Add note error:', err);
      setError('Failed to add note');
    } finally {
      setNoteLoading(false);
    }
  };

  const handleStartEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditNoteText(note.note_text);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteText.trim() || !selectedMessage) return;
    
    setNoteLoading(true);
    try {
      const response = await reportingAPI.updateNote(selectedMessage.id, noteId, editNoteText.trim());
      setNotes(notes.map(n => n.id === noteId ? response.data.note : n));
      setEditingNoteId(null);
      setEditNoteText('');
    } catch (err) {
      console.error('Update note error:', err);
      setError('Failed to update note');
    } finally {
      setNoteLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    setNoteLoading(true);
    try {
      await reportingAPI.deleteNote(selectedMessage.id, noteId);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Delete note error:', err);
      setError('Failed to delete note');
    } finally {
      setNoteLoading(false);
    }
  };

  // Feedback handlers
  const handleSubmitFeedback = async (feedback) => {
    setFeedbackLoading(true);
    try {
      const response = await reportingAPI.submitFeedback(selectedMessage.id, feedback);
      const updates = { user_feedback: feedback, feedback_at: response.data.feedback_at };
      setSelectedMessage(prev => ({ ...prev, ...updates }));
      setAllMessages(prev =>
        prev.map(msg => msg.id === selectedMessage.id ? { ...msg, ...updates } : msg)
      );
    } catch (err) {
      console.error('Feedback error:', err);
      setError('Failed to save feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleRemoveFeedback = async () => {
    setFeedbackLoading(true);
    try {
      await reportingAPI.removeFeedback(selectedMessage.id);
      const updates = { user_feedback: null, feedback_at: null };
      setSelectedMessage(prev => ({ ...prev, ...updates }));
      setAllMessages(prev =>
        prev.map(msg => msg.id === selectedMessage.id ? { ...msg, ...updates } : msg)
      );
    } catch (err) {
      console.error('Remove feedback error:', err);
      setError('Failed to remove feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleAcknowledgeMoneySent = async () => {
    if (!selectedMessage) return;
    setAcknowledgeLoading(true);
    try {
      await reportingAPI.acknowledgeMoneySent(selectedMessage.id);
      const updates = { money_sent_acknowledged: true, money_sent_acknowledged_at: new Date().toISOString() };
      setSelectedMessage(prev => ({ ...prev, ...updates }));
      setAllMessages(prev =>
        prev.map(msg => msg.id === selectedMessage.id ? { ...msg, ...updates } : msg)
      );
    } catch (err) {
      console.error('Acknowledge money sent error:', err);
      setError('Failed to acknowledge');
    } finally {
      setAcknowledgeLoading(false);
    }
  };

  const handleToggleMoneySent = async () => {
    if (!selectedMessage) return;
    const newValue = !selectedMessage.money_sent;
    const confirmMessage = newValue
      ? 'Are you sure you want to mark this as money sent?'
      : 'Are you sure you want to remove the money sent flag?';
    setConfirmDialog({
      message: confirmMessage,
      onConfirm: async () => {
        setConfirmDialog(null);
        setMoneySentLoading(true);
        try {
          await reportingAPI.updateMessageStatus(selectedMessage.id, { money_sent: newValue });
          const updates = {
            money_sent: newValue,
            money_sent_marked_at: newValue ? new Date().toISOString() : null,
            ...(newValue ? {} : { money_sent_acknowledged: false, money_sent_acknowledged_at: null }),
          };
          setSelectedMessage(prev => ({ ...prev, ...updates }));
          setAllMessages(prev =>
            prev.map(msg => msg.id === selectedMessage.id ? { ...msg, ...updates } : msg)
          );
        } catch (err) {
          console.error('Toggle money sent error:', err);
          setError('Failed to update money sent status');
        } finally {
          setMoneySentLoading(false);
        }
      },
    });
  };

  const handleTogglePiiDisclosed = async () => {
    if (!selectedMessage) return;
    const newValue = !selectedMessage.pii_disclosed;
    const confirmMessage = newValue
      ? 'Are you sure you want to mark this as PII disclosed?'
      : 'Are you sure you want to remove the PII disclosed flag?';
    setConfirmDialog({
      message: confirmMessage,
      onConfirm: async () => {
        setConfirmDialog(null);
        setPiiDisclosedLoading(true);
        try {
          await reportingAPI.updateMessageStatus(selectedMessage.id, { pii_disclosed: newValue });
          const updates = {
            pii_disclosed: newValue,
            pii_disclosed_marked_at: newValue ? new Date().toISOString() : null,
            ...(newValue ? {} : { pii_disclosed_acknowledged: false, pii_disclosed_acknowledged_at: null }),
          };
          setSelectedMessage(prev => ({ ...prev, ...updates }));
          setAllMessages(prev =>
            prev.map(msg => msg.id === selectedMessage.id ? { ...msg, ...updates } : msg)
          );
        } catch (err) {
          console.error('Toggle PII disclosed error:', err);
          setError('Failed to update PII disclosed status');
        } finally {
          setPiiDisclosedLoading(false);
        }
      },
    });
  };

  const handleAcknowledgePiiDisclosed = async () => {
    if (!selectedMessage) return;
    setAcknowledgePiiLoading(true);
    try {
      await reportingAPI.acknowledgePiiDisclosed(selectedMessage.id);
      const updates = { pii_disclosed_acknowledged: true, pii_disclosed_acknowledged_at: new Date().toISOString() };
      setSelectedMessage(prev => ({ ...prev, ...updates }));
      setAllMessages(prev =>
        prev.map(msg => msg.id === selectedMessage.id ? { ...msg, ...updates } : msg)
      );
    } catch (err) {
      console.error('Acknowledge PII disclosed error:', err);
      setError('Failed to acknowledge');
    } finally {
      setAcknowledgePiiLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedMessage(null);
    setNotes([]);
    setNewNote('');
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const handleViewDetails = async (message) => {
    try {
      setDetailLoading(true);
      const response = await reportingAPI.getMessage(message.id);
      
      // Extract message from response and parse signals
      const messageData = response.data.message;
      
      // Parse extracted_signals if it's a JSON string
      if (messageData.extracted_signals && typeof messageData.extracted_signals === 'string') {
        messageData.extracted_signals = JSON.parse(messageData.extracted_signals);
      }
      
      // Add signals array for easy access
      messageData.signals = messageData.extracted_signals?.signals || [];
      
      setSelectedMessage(messageData);
      
      // Auto-mark as read when viewing
      if (!messageData.is_read) {
        handleMessageAction(messageData.id, { is_read: true });
      }

      // Load notes
      const notesResponse = await reportingAPI.getNotes(message.id);
      setNotes(notesResponse.data.notes || []);
    } catch (err) {
      console.error('Error loading message details:', err);
      setError('Failed to load message details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleMessageAction = (messageId, updates) => {
    // Optimistic update on local allMessages
    setAllMessages(prev =>
      prev.map(msg => msg.id === messageId ? { ...msg, ...updates } : msg)
    );

    // Sync unread badge count in context
    if (updates.is_read !== undefined) {
      setStats(prev => prev ? {
        ...prev,
        unread_count: prev.unread_count + (updates.is_read ? -1 : 1)
      } : prev);
    }

    // Update selected message immediately
    if (selectedMessage && selectedMessage.id === messageId) {
      setSelectedMessage(prev => ({ ...prev, ...updates }));
    }

    // Call API in background
    reportingAPI.updateMessageStatus(messageId, updates).catch(err => {
      console.error('Failed to update message:', err);
      fetchAllMessages(true); // silent revert
    });
  };

  const handleToggleRead = () => {
    if (selectedMessage) {
      handleMessageAction(selectedMessage.id, { is_read: !selectedMessage.is_read });
    }
  };

  const handleToggleFlag = () => {
    if (selectedMessage) {
      handleMessageAction(selectedMessage.id, { is_flagged: !selectedMessage.is_flagged });
    }
  };

  const handleMarkNotFraud = () => {
    if (selectedMessage) {
      handleMessageAction(selectedMessage.id, { marked_not_fraud: !selectedMessage.marked_not_fraud });
    }
  };

  const handleSetRisk = (risk) => {
    if (selectedMessage) {
      handleMessageAction(selectedMessage.id, { risk_override: risk });
    }
  };

  const handleCloseDetail = () => {
    setSelectedMessage(null);
  };

  const getRiskColor = (highRisk) => {
    return highRisk ? 'error' : 'success';
  };

  const getRiskLabel = (highRisk) => {
    return highRisk ? 'High Risk' : 'Low Risk';
  };

  const getRiskIcon = (highRisk) => {
    return highRisk ? <WarningIcon /> : <CheckCircleIcon />;
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Messages</Typography>
          <Typography variant="body2" color="text.secondary">
            Showing {paginatedMessages.length} of {totalMessages} messages
            {channelFilter !== 'all' && ` (${channelFilter.toUpperCase()})`}
            {selectedClientId !== 'all' && clients.length > 0 && ` — ${clients.find(s => s.id === selectedClientId)?.name || 'Selected'}`}
            {selectedMessages.length > 0 && ` • ${selectedMessages.length} selected`}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchAllMessages(true)} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={handleExport}
            startIcon={<DownloadIcon />}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Bulk Actions Bar */}
      {selectedMessages.length > 0 && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, bgcolor: 'action.hover' }}>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ mr: { xs: 0, sm: 1 } }}>
              {selectedMessages.length} message{selectedMessages.length !== 1 ? 's' : ''} selected
            </Typography>
            <ButtonGroup size="small" disabled={bulkActionLoading} sx={{ flexWrap: 'wrap' }}>
              <Button onClick={() => handleBulkUpdate({ is_read: true })} startIcon={<MarkEmailReadIcon />} sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                Read
              </Button>
              <Button onClick={() => handleBulkUpdate({ is_read: false })} startIcon={<MarkEmailUnreadIcon />} sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                Unread
              </Button>
              <Button onClick={() => handleBulkUpdate({ is_flagged: true })} startIcon={<FlagIcon />} sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}>
                Flag
              </Button>
            </ButtonGroup>
            <Button
              size="small"
              onClick={() => setSelectedMessages([])}
              disabled={bulkActionLoading}
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
            >
              Clear
            </Button>
            {bulkActionLoading && <CircularProgress size={20} />}
          </Box>
        </Paper>
      )}

      {/* Search & Person Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {(user?.role === 'caregiver' || user?.role === 'both') && clients.length > 0 && (
            <Grid item xs={12} sm={4} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Person</InputLabel>
                <Select
                  value={selectedClientId}
                  label="Person"
                  onChange={(e) => { setSelectedClientId(e.target.value); setPage(0); }}
                >
                  <MenuItem value="all">All People</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={(user?.role === 'caregiver' || user?.role === 'both') && clients.length > 0 ? 8 : 12} md={(user?.role === 'caregiver' || user?.role === 'both') && clients.length > 0 ? 9 : 12}>
            <TextField
              size="small"
              fullWidth
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by message text or user name..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {/* Time period - buttons on desktop, dropdown on mobile */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
            <Button
              variant={activeQuickFilter === 'today' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setQuickFilter('today')}
            >
              Today
            </Button>
            <Button
              variant={activeQuickFilter === 'week' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setQuickFilter('week')}
            >
              This Week
            </Button>
            <Button
              variant={activeQuickFilter === 'high-risk' ? 'contained' : 'outlined'}
              size="small"
              color="error"
              onClick={() => setQuickFilter('high-risk')}
            >
              High Risk
            </Button>
          </Box>
          <FormControl size="small" sx={{ display: { xs: 'flex', sm: 'none' }, minWidth: 120 }}>
            <InputLabel>Time</InputLabel>
            <Select
              value={activeQuickFilter || ''}
              label="Time"
              onChange={(e) => setQuickFilter(e.target.value || 'clear')}
            >
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="high-risk">High Risk</MenuItem>
            </Select>
          </FormControl>

          {/* Channel - buttons on desktop, dropdown on mobile */}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'flex' } }} />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
            <Button
              variant={channelFilter === 'all' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => { setChannelFilter('all'); setPage(0); }}
            >
              All
            </Button>
            <Button
              variant={channelFilter === 'sms' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<SmsIcon />}
              onClick={() => { setChannelFilter('sms'); setPage(0); }}
            >
              SMS
            </Button>
            <Button
              variant={channelFilter === 'voice' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<PhoneIcon />}
              onClick={() => { setChannelFilter('voice'); setPage(0); }}
            >
              Voice
            </Button>
            <Button
              variant={channelFilter === 'email' ? 'contained' : 'outlined'}
              size="small"
              startIcon={<EmailIcon />}
              onClick={() => { setChannelFilter('email'); setPage(0); }}
            >
              Email
            </Button>
          </Box>
          <FormControl size="small" sx={{ display: { xs: 'flex', sm: 'none' }, minWidth: 120 }}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={channelFilter}
              label="Channel"
              onChange={(e) => { setChannelFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">All Channels</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="voice">Voice</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </Select>
          </FormControl>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'flex' } }} />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={highRiskOnly}
                onChange={(e) => setHighRiskOnly(e.target.checked)}
              />
            }
            label={<Typography variant="body2">High Risk Only</Typography>}
            sx={{ ml: 0 }}
          />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: 'none', sm: 'flex' } }} />

          <TextField
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            label="From"
            sx={{ width: { xs: '100%', sm: 160 } }}
          />
          <TextField
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            label="To"
            sx={{ width: { xs: '100%', sm: 160 } }}
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            size="small"
            onClick={() => setQuickFilter('clear')}
            disabled={!activeQuickFilter && !searchText && !startDate && !endDate && channelFilter === 'all' && selectedClientId === 'all' && !highRiskOnly}
          >
            Clear All
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {totalMessages === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No messages found
          </Typography>
          <Typography color="text.secondary">
            {highRiskOnly
              ? 'No high-risk messages detected yet.'
              : 'No messages have been analyzed yet.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedMessages.length > 0 && selectedMessages.length < paginatedMessages.length}
                    checked={paginatedMessages.length > 0 && selectedMessages.length === paginatedMessages.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Date/Time</TableCell>
                <TableCell>User</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Channel</TableCell>
                <TableCell>Message Preview</TableCell>
                <TableCell align="center">Risk</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Signals</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedMessages.map((message) => (
                <TableRow
                  key={message.id}
                  hover
                  onClick={() => handleViewDetails(message)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: message.money_sent ? '#fef2f2' : message.pii_disclosed ? '#fff7ed' : (message.is_read ? 'inherit' : 'action.hover'),
                    borderLeft: message.money_sent ? '4px solid #dc2626' : message.pii_disclosed ? '4px solid #ea580c' : 'none',
                    fontWeight: message.is_read ? 'normal' : 'bold'
                  }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedMessages.includes(message.id)}
                      onChange={() => handleSelectMessage(message.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {message.is_flagged && (
                        <Tooltip title="Flagged for follow-up">
                          <FlagIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      {!message.is_read && (
                        <Tooltip title="Unread">
                          <Box 
                            sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main' 
                            }} 
                          />
                        </Tooltip>
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight={message.is_read ? 'normal' : 'bold'}>
                          {format(new Date(message.received_at), 'MMM d, yyyy')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(message.received_at), 'h:mm a')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={message.is_read ? 'normal' : 'bold'}>
                      {message.user_name || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Chip
                      label={message.channel.toUpperCase()}
                      size="small"
                      color={
                        message.channel === 'sms' ? 'primary' :
                        message.channel === 'voice' ? 'secondary' :
                        message.channel === 'email' ? 'info' : 'default'
                      }
                      sx={{ minWidth: 60 }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: { xs: 150, sm: 200, md: 300 } }}>
                    <Typography 
                      variant="body2" 
                      noWrap
                      fontWeight={message.is_read ? 'normal' : 'bold'}
                    >
                      {message.message_text?.substring(0, 100)}...
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                      {message.money_sent && (
                        <Tooltip title="Money sent reported">
                          <Chip
                            icon={<MoneyOffIcon />}
                            label="Money Sent"
                            size="small"
                            color="error"
                          />
                        </Tooltip>
                      )}
                      {message.pii_disclosed && (
                        <Tooltip title="Personal information shared">
                          <Chip
                            icon={<SecurityIcon />}
                            label="PII Shared"
                            size="small"
                            sx={{ bgcolor: '#fff7ed', color: '#ea580c', borderColor: '#ea580c', border: '1px solid' }}
                          />
                        </Tooltip>
                      )}
                      {message.risk_override ? (
                        <Tooltip title={`Risk manually set to ${message.risk_override}`}>
                          <Chip
                            icon={getRiskIcon(message.risk_override === 'high')}
                            label={message.risk_override === 'high' ? 'High Risk (Manual)' : 'Low Risk (Manual)'}
                            color={message.risk_override === 'high' ? 'error' : 'success'}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : message.marked_not_fraud ? (
                        <Tooltip title="Marked as not fraud">
                          <Chip
                            icon={<BlockIcon />}
                            label="Not Fraud"
                            size="small"
                            color="default"
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          icon={getRiskIcon(message.high_risk)}
                          label={getRiskLabel(message.high_risk)}
                          color={getRiskColor(message.high_risk)}
                          size="small"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip label={message.signal_count} size="small" />
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleViewDetails(message)}
                      title="View details"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalMessages}
            page={page}
            onPageChange={(e, newPage) => {
              setPage(newPage);
              setSelectedMessages([]);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
              setSelectedMessages([]);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmDialog?.onConfirm}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Message Detail Dialog */}
      <Dialog
        open={!!selectedMessage}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {detailLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : selectedMessage ? (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="h6">Message Analysis</Typography>
                <Chip
                  icon={getRiskIcon(selectedMessage.high_risk)}
                  label={getRiskLabel(selectedMessage.high_risk)}
                  color={getRiskColor(selectedMessage.high_risk)}
                />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Money Sent Banner */}
              {selectedMessage.money_sent ? (
                <Alert
                  severity="error"
                  icon={<MoneyOffIcon />}
                  sx={{ mb: 2 }}
                  action={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {!selectedMessage.money_sent_acknowledged && (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleAcknowledgeMoneySent}
                          disabled={acknowledgeLoading}
                        >
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleToggleMoneySent}
                        disabled={moneySentLoading}
                      >
                        Unmark
                      </Button>
                    </Box>
                  }
                >
                  {selectedMessage.money_sent_acknowledged ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                      <Typography variant="body2">
                        Acknowledged{selectedMessage.money_sent_acknowledged_at
                          ? ` on ${format(new Date(selectedMessage.money_sent_acknowledged_at), 'MMM d, yyyy h:mm a')}`
                          : ''}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      <strong>This person reported sending money</strong> in response to a message we flagged.
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<MoneyOffIcon />}
                  onClick={handleToggleMoneySent}
                  disabled={moneySentLoading}
                  sx={{ mb: 1, mr: 1 }}
                >
                  Mark as Money Sent
                </Button>
              )}

              {/* Money Sent Recovery Guidance */}
              {selectedMessage.money_sent && (
                <Accordion variant="outlined" sx={{ mb: 2, bgcolor: '#fef2f2', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" color="error.dark">Steps to consider</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
                      <li>Contact your bank to ask about reversing the transaction</li>
                      <li>Report to the FTC at reportfraud.ftc.gov</li>
                      <li>If gift cards were involved, contact the gift card company</li>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* PII Disclosed Banner */}
              {selectedMessage.pii_disclosed ? (
                <Alert
                  severity="warning"
                  icon={<SecurityIcon sx={{ color: '#ea580c' }} />}
                  sx={{ mb: 2, '& .MuiAlert-icon': { color: '#ea580c' } }}
                  action={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {!selectedMessage.pii_disclosed_acknowledged && (
                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleAcknowledgePiiDisclosed}
                          disabled={acknowledgePiiLoading}
                        >
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleTogglePiiDisclosed}
                        disabled={piiDisclosedLoading}
                      >
                        Unmark
                      </Button>
                    </Box>
                  }
                >
                  {selectedMessage.pii_disclosed_acknowledged ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleOutlineIcon fontSize="small" color="success" />
                      <Typography variant="body2">
                        Acknowledged{selectedMessage.pii_disclosed_acknowledged_at
                          ? ` on ${format(new Date(selectedMessage.pii_disclosed_acknowledged_at), 'MMM d, yyyy h:mm a')}`
                          : ''}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2">
                      <strong>This person reported sharing personal information</strong> in response to a message we flagged.
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mb: 1, mr: 1, color: '#ea580c', borderColor: '#ea580c' }}
                  startIcon={<SecurityIcon />}
                  onClick={handleTogglePiiDisclosed}
                  disabled={piiDisclosedLoading}
                >
                  Mark as PII Disclosed
                </Button>
              )}

              {/* PII Disclosed Recovery Guidance */}
              {selectedMessage.pii_disclosed && (
                <Accordion variant="outlined" sx={{ mb: 3, bgcolor: '#fff7ed', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ color: '#ea580c' }}>Steps to consider</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
                      <li>If SSN was shared: Consider a credit freeze at all three bureaus (Equifax, Experian, TransUnion)</li>
                      <li>If passwords were shared: Change those passwords and enable two-factor authentication</li>
                      <li>If bank account numbers were shared: Contact your bank to monitor for unauthorized access</li>
                      <li>Consider placing a fraud alert on credit reports</li>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Metadata */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Received
                  </Typography>
                  <Typography variant="body2">
                    {selectedMessage.received_at 
                      ? format(new Date(selectedMessage.received_at), 'PPpp')
                      : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Channel
                  </Typography>
                  <Chip
                    label={selectedMessage.channel.toUpperCase()}
                    size="small"
                    color={
                      selectedMessage.channel === 'sms' ? 'primary' :
                      selectedMessage.channel === 'voice' ? 'secondary' :
                      selectedMessage.channel === 'email' ? 'info' : 'default'
                    }
                    sx={{ mt: 0.5, minWidth: 60 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    User
                  </Typography>
                  <Typography variant="body2">
                    {selectedMessage.user_name || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Signals Detected
                  </Typography>
                  <Typography variant="body2">
                    {selectedMessage.signal_count}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Original Message */}
              <Typography variant="subtitle2" gutterBottom>
                Original Message
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.message_text}
                </Typography>
              </Paper>

              {/* Detected Signals */}
              {selectedMessage.signals && selectedMessage.signals.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Detected Fraud Signals ({selectedMessage.signals.length})
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    {selectedMessage.signals.map((signal, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{ p: 2, mb: 1, bgcolor: 'error.light', borderColor: 'error.main' }}
                      >
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          {signal.pattern_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Category: {signal.category}
                        </Typography>
                        {signal.matched_text && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Matched: "{signal.matched_text}"
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Box>
                </>
              )}

              {/* Response */}
              <Typography variant="subtitle2" gutterBottom>
                Response Sent
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.response_text}
                </Typography>
              </Paper>

              {/* Feedback Section */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Was this analysis accurate?
              </Typography>
              
              {selectedMessage.user_feedback ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Chip 
                    icon={selectedMessage.user_feedback === 'confirmed_scam' ? <ThumbDownIcon /> : <ThumbUpIcon />}
                    label={selectedMessage.user_feedback === 'confirmed_scam' ? 'Marked as Scam' : 'Marked as Legitimate'}
                    color={selectedMessage.user_feedback === 'confirmed_scam' ? 'error' : 'success'}
                  />
                  <Tooltip title="Undo feedback">
                    <IconButton 
                      size="small" 
                      onClick={handleRemoveFeedback}
                      disabled={feedbackLoading}
                    >
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {selectedMessage.feedback_at && (
                    <Typography variant="caption" color="text.secondary">
                      Submitted {format(new Date(selectedMessage.feedback_at), 'MMM d, yyyy h:mm a')}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Help us improve by letting us know if our analysis was correct.
                  </Typography>
                  <ButtonGroup size="small" disabled={feedbackLoading}>
                    <Button 
                      startIcon={<ThumbDownIcon />}
                      onClick={() => handleSubmitFeedback('confirmed_scam')}
                      color="error"
                      variant="outlined"
                    >
                      This was a scam
                    </Button>
                    <Button 
                      startIcon={<ThumbUpIcon />}
                      onClick={() => handleSubmitFeedback('not_scam')}
                      color="success"
                      variant="outlined"
                    >
                      This was legitimate
                    </Button>
                  </ButtonGroup>
                  {feedbackLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
                </Box>
              )}

              {/* Notes Section */}
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Notes & Comments
              </Typography>
              
              {/* Existing Notes */}
              {notes.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  {notes.map((note) => (
                    <Paper key={note.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                      {editingNoteId === note.id ? (
                        <Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={editNoteText}
                            onChange={(e) => setEditNoteText(e.target.value)}
                            disabled={noteLoading}
                          />
                          <Box display="flex" gap={1} mt={1}>
                            <Button 
                              size="small" 
                              onClick={() => handleUpdateNote(note.id)}
                              disabled={noteLoading || !editNoteText.trim()}
                            >
                              Save
                            </Button>
                            <Button 
                              size="small" 
                              onClick={handleCancelEditNote}
                              disabled={noteLoading}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                            <Box>
                              <Typography variant="subtitle2">{note.author_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(note.created_at).toLocaleString()}
                                {note.edited && ' (edited)'}
                              </Typography>
                            </Box>
                            <Box>
                              <IconButton 
                                size="small" 
                                onClick={() => handleStartEditNote(note)}
                                disabled={noteLoading}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteNote(note.id)}
                                disabled={noteLoading}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {note.note_text}
                          </Typography>
                        </>
                      )}
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No notes yet. Add one below.
                </Typography>
              )}

              {/* Add New Note */}
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add a note or comment..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  disabled={noteLoading}
                />
                <Button 
                  variant="contained" 
                  sx={{ mt: 1 }}
                  onClick={handleAddNote}
                  disabled={noteLoading || !newNote.trim()}
                >
                  Add Note
                </Button>
              </Box>
            </DialogContent>
            <DialogActions sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: { xs: 1, sm: 0 },
              px: { xs: 2, sm: 3 },
              py: 2
            }}>
              {/* Top row on mobile: flag/read + close */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                <Box>
                  <Tooltip title={selectedMessage.is_flagged ? "Unflag" : "Flag for follow-up"}>
                    <IconButton
                      onClick={handleToggleFlag}
                      color={selectedMessage.is_flagged ? "warning" : "default"}
                    >
                      {selectedMessage.is_flagged ? <FlagIcon /> : <FlagOutlinedIcon />}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={selectedMessage.is_read ? "Mark as unread" : "Mark as read"}>
                    <IconButton onClick={handleToggleRead} color="primary">
                      {selectedMessage.is_read ? <MarkEmailUnreadIcon /> : <MarkEmailReadIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <Button onClick={handleCloseDetail} variant="contained" size="small">
                    Close
                  </Button>
                </Box>
              </Box>

              {/* Risk override buttons - scroll horizontally on mobile */}
              <Box sx={{ overflowX: 'auto', width: { xs: '100%', sm: 'auto' } }}>
                <ButtonGroup size="small" variant="outlined" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Mark as high risk">
                    <Button
                      onClick={() => handleSetRisk('high')}
                      variant={selectedMessage.risk_override === 'high' ? 'contained' : 'outlined'}
                      color="error"
                      startIcon={<TrendingUpIcon />}
                    >
                      High Risk
                    </Button>
                  </Tooltip>
                  <Tooltip title="Mark as low risk">
                    <Button
                      onClick={() => handleSetRisk('low')}
                      variant={selectedMessage.risk_override === 'low' ? 'contained' : 'outlined'}
                      color="success"
                      startIcon={<TrendingDownIcon />}
                    >
                      Low Risk
                    </Button>
                  </Tooltip>
                  <Tooltip title={selectedMessage.marked_not_fraud ? "Unmark as not fraud" : "Mark as not fraud - false positive"}>
                    <Button
                      onClick={handleMarkNotFraud}
                      variant={selectedMessage.marked_not_fraud ? 'contained' : 'outlined'}
                      startIcon={<BlockIcon />}
                      color={selectedMessage.marked_not_fraud ? 'default' : 'inherit'}
                    >
                      {selectedMessage.marked_not_fraud ? 'Marked Not Fraud' : 'Not Fraud'}
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              </Box>

              {/* Close button - hidden on mobile (shown inline above) */}
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Button onClick={handleCloseDetail} variant="contained">
                  Close
                </Button>
              </Box>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
};

export default Messages;
